import { useRef } from 'react';
import type { CSSProperties } from 'react';
import { useMotionEnabled } from '../lib/motion';
import { useIdleTurns } from '../lib/useIdleTurns';
import { useInViewActive } from '../lib/useInViewActive';

/**
 * The logo's breathing red glow, restructured for the compositor
 * (2026-07-24): the old .logo-breathe keyframed `filter: drop-shadow(...)`,
 * which re-rasterizes the shadow on the CPU every frame of the 5.5s loop.
 * Here the art is stacked twice with STATIC filters — a dim base and a hot
 * overlay — and only the overlay's OPACITY animates, so after the first
 * rasterization the breathe is a pure GPU composite.
 *
 * Spiral idle spin (client request 2026-07-24): the swirl disc at the
 * mark's center idles on the shared dwell-and-turn rhythm (useIdleTurns,
 * same vocabulary as the BreakLine center star) — dwell a few seconds,
 * then one smooth CLOCKWISE revolution. The disc is isolated WITHOUT
 * editing the art: the swirl is fused into one path with the letter
 * flourishes, so instead the static layers wear a mask with a disc-shaped
 * HOLE and a second pair of layers — masked to ONLY that disc — rotates
 * about its center. A circular mask centered on the rotation origin is
 * rotation-invariant, so the window never moves while its content spins.
 *
 * Each turn is a FULL 360°, not the center star's 90°: the swirl was
 * measured to have no rotational symmetry short of a full turn (its solid
 * core is circular, but the crescent cuts are a true spiral), so any other
 * resting angle would visibly misalign the cuts against the static arms.
 * Mid-spin the arms "unlatch" at the seam and re-latch exactly at rest —
 * the mechanical read is intentional. The spin ease deliberately has NO
 * overshoot: past 360° the art would shear back at the latch.
 *
 * Mask geometry was measured from the rasterized art via 90°-symmetry
 * search (scratch scripts, 2026-07-24): disc center/radius per asset, in %
 * of the art box; rx/ry are the same viewBox length expressed against each
 * axis, so the ellipse renders as a true circle. Reduced motion (or an src
 * with no mark config) renders the plain two-layer version — the exact
 * pre-spin appearance.
 */

type SpinMark = { cx: number; cy: number; rx: number; ry: number };

const SPIN_MARKS: Record<string, SpinMark> = {
  // viewBox 200 200 852.99 500.19; disc center (627.25, 614.75), r=30u.
  '/assets/images/logos/level-awakening.svg': { cx: 50.09, cy: 82.92, rx: 3.517, ry: 5.998 },
  // viewBox 199.97 200.08 293.03 123.34; disc center (346.5, 293.5), r=12u.
  '/assets/images/logos/awakening.svg': { cx: 50.0, cy: 75.74, rx: 4.095, ry: 9.729 },
};

const SPIN_MS = 1250;
const SPIN_EASE = 'cubic-bezier(0.5, 0.05, 0.2, 1)';
const SPIN_DWELL_MIN_MS = 2800;
const SPIN_DWELL_RANGE_MS = 3200;
const FULL_TURN_CW = () => 360;

// The 97→100% band is ~1px of anti-aliasing at rendered size; the two
// masks cross-fade over the same band, so their alphas always sum to 1
// and no seam ring can show.
const holeMask = (m: SpinMark) =>
  `radial-gradient(ellipse ${m.rx}% ${m.ry}% at ${m.cx}% ${m.cy}%, transparent 97%, black 100%)`;
const discMask = (m: SpinMark) =>
  `radial-gradient(ellipse ${m.rx}% ${m.ry}% at ${m.cx}% ${m.cy}%, black 97%, transparent 100%)`;
const maskStyle = (mask: string): CSSProperties => ({
  WebkitMaskImage: mask,
  maskImage: mask,
});

const maskSupported =
  typeof CSS !== 'undefined' &&
  (CSS.supports('mask-image', 'radial-gradient(#000, transparent)') ||
    CSS.supports('-webkit-mask-image', 'radial-gradient(#000, transparent)'));

export function BreathingLogo({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  /** Sizing/visibility classes for the wrapper (e.g. `hidden sm:block w-…`). */
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const motionOn = useMotionEnabled();
  const mark = SPIN_MARKS[src];
  const spinOn = Boolean(mark) && maskSupported && motionOn;
  const active = useInViewActive(ref) && spinOn;
  const angle = useIdleTurns(active, SPIN_DWELL_MIN_MS, SPIN_DWELL_RANGE_MS, FULL_TURN_CW);

  if (!spinOn) {
    return (
      <span ref={ref} className={`relative ${className ?? ''}`}>
        <img src={src} alt={alt} className="glow-logo-dim block h-auto w-full" />
        <img
          src={src}
          alt=""
          aria-hidden="true"
          className="glow-logo-hot glow-breathe absolute inset-0 h-auto w-full"
        />
      </span>
    );
  }

  // The mask lives on the IMG and the glow filter on a WRAPPING span, never
  // both on one element: a mask's painting area is the border box, so
  // masking a filtered img clips the drop-shadow glow to a hard rectangle —
  // the same clipping bug the baked Figma filters had (see the glow policy
  // in index.css). With the filter outside the mask, the glow traces the
  // masked art and paints past the box freely.
  return (
    <span ref={ref} className={`relative ${className ?? ''}`}>
      {/* Static art with the swirl disc punched out. */}
      <span className="glow-logo-dim block">
        <img
          src={src}
          alt={alt}
          className="block h-auto w-full"
          style={maskStyle(holeMask(mark))}
        />
      </span>
      <span aria-hidden="true" className="glow-logo-hot glow-breathe absolute inset-0 block">
        <img src={src} alt="" className="block h-auto w-full" style={maskStyle(holeMask(mark))} />
      </span>
      {/* The disc window, spinning about its own center. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          transform: `rotate(${angle}deg)`,
          transition: `transform ${SPIN_MS}ms ${SPIN_EASE}`,
          transformOrigin: `${mark.cx}% ${mark.cy}%`,
        }}
      >
        <span className="glow-logo-dim absolute inset-0 block">
          <img src={src} alt="" className="block h-auto w-full" style={maskStyle(discMask(mark))} />
        </span>
        <span className="glow-logo-hot glow-breathe absolute inset-0 block">
          <img src={src} alt="" className="block h-auto w-full" style={maskStyle(discMask(mark))} />
        </span>
      </span>
    </span>
  );
}
